import { ReferenceContactId, RegionId } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { MessageCompose } from "components/messages/message-compose";
import { ReferralTierFilter } from "components/references/dashboard-reference/referral-tier-filter";
import { trpc } from "components/rpc/trpc";
import { Spinner } from "components/ui/spinner";
import { AdvancedSearchEntityType } from "generated/graphql";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { usePhoneLines } from "store/selectors/telecom";
import { agendaSlice } from "store/slices/agenda";
import { ApplicationState } from "store/state";
import { handleEvent } from "util/user-events";
import { AgendaProvisionBanner } from "./agenda-actions";
import { AgendaAwaitingOutcome } from "./agenda-awaiting-outcome";
import { AgendaContactCard } from "./agenda-contact-card";
import { AgendaHardLandscape } from "./agenda-hard-landscape";
import { AgendaRecentlyCompleted } from "./agenda-recently-completed";
import { AgendaSnoozedSection } from "./agenda-snoozed-section";
import {
    agendaNeedsPhoneProvisioning,
    filterGroupsByTier,
    groupDueTouchpoints,
} from "./marketing-plan-agenda";
import { PlanHealthBanner } from "./plan-health-banner";
import { PlanModal } from "./plan-modal";
import { useAgendaRefreshWithCompletionDetection } from "./use-agenda-refresh";
import { usePlanHealth } from "./use-plan-health";

export const AgendaTab: React.FC<unknown> = () => {
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const visitsEnabled = useFeature("visits");
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const invalidateAgenda = useAgendaRefreshWithCompletionDetection();
    const phoneLines = usePhoneLines();
    const [composingContactId, setComposingContactId] =
        useState<ReferenceContactId | null>(null);
    const [selectedTierIds, setSelectedTierIds] = useState<string[]>([]);
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
    const dispatch = useDispatch();
    const recentlyCompleted = useSelector(
        (state: ApplicationState) => state.agenda.recentlyCompleted,
    );
    const planHealth = usePlanHealth();

    const dueQuery = useQuery({
        ...trpcClient.marketingPlaybook.dueTouchpoints.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });

    const snoozesQuery = useQuery({
        ...trpcClient.marketingPlaybook.activeSnoozes.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });

    useEffect(() => {
        dispatch(agendaSlice.actions.clearRecentlyCompleted());
    }, [dispatch, regionId]);

    const dueRows = dueQuery.data ?? [];
    const hasPhoneLine = phoneLines.length > 0;
    const groups = groupDueTouchpoints(dueRows);
    const visibleGroups = filterGroupsByTier(groups, selectedTierIds);
    const showProvisionBanner =
        !dueQuery.isLoading && agendaNeedsPhoneProvisioning(groups, hasPhoneLine);

    const openCompose = (contactId: string) => {
        handleEvent("marketing-plan-send-email");
        setComposingContactId(contactId as ReferenceContactId);
    };

    const closeCompose = () => {
        setComposingContactId(null);
        invalidateAgenda();
    };

    return (
        <>
            <div className="space-y-4">
                <AgendaHardLandscape visitsEnabled={visitsEnabled} />
                <AgendaAwaitingOutcome visitsEnabled={visitsEnabled} />
                <div className="flex items-center justify-between gap-3 pt-2">
                    <div className="flex items-center gap-2">
                        <span className="h-6 w-1 flex-shrink-0 rounded-full bg-brand-500" />
                        <h2 className="twoverride text-lg font-semibold text-gray-800">
                            Referral Outreach
                        </h2>
                    </div>
                    <ReferralTierFilter
                        value={selectedTierIds}
                        onChange={setSelectedTierIds}
                    />
                </div>
                <PlanHealthBanner
                    planHealth={planHealth}
                    onViewPlan={() => setIsPlanModalOpen(true)}
                    noPlanMessage="Once you set up a marketing plan, we'll show which referral sources are due for outreach here."
                />
                {showProvisionBanner ? <AgendaProvisionBanner /> : null}
                <AgendaRecentlyCompleted items={recentlyCompleted} />
                {dueQuery.isLoading ? (
                    <div className="flex justify-center rounded-lg bg-white p-12 ring-1 ring-gray-300">
                        <Spinner />
                    </div>
                ) : groups.length === 0 ? (
                    planHealth.hasNoTouchpoints ? null : (
                        <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 ring-1 ring-gray-300">
                            All caught up — no referral sources are due for outreach right
                            now.
                        </div>
                    )
                ) : visibleGroups.length === 0 ? (
                    <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 ring-1 ring-gray-300">
                        No referral sources match the selected tier.
                    </div>
                ) : (
                    <ul className="space-y-3 sm:space-y-4">
                        {visibleGroups.map(group => (
                            <li key={group.contactId}>
                                <AgendaContactCard
                                    group={group}
                                    hasPhoneLine={hasPhoneLine}
                                    visitsEnabled={visitsEnabled}
                                    onSendEmail={openCompose}
                                />
                            </li>
                        ))}
                    </ul>
                )}
                <AgendaSnoozedSection snoozes={snoozesQuery.data ?? []} />
            </div>
            {composingContactId ? (
                <MessageCompose
                    entityId={composingContactId}
                    entityType={AdvancedSearchEntityType.ReferenceContact}
                    onDismiss={closeCompose}
                />
            ) : null}
            <PlanModal
                isOpen={isPlanModalOpen}
                onDismiss={() => setIsPlanModalOpen(false)}
            />
        </>
    );
};
